import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LOGO_URL = "https://storage.googleapis.com/uxpilot-auth.appspot.com/locals-assets/locals-logo.png";

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="font-heading font-bold text-xl text-text-primary mb-3">{title}</h2>
    <div className="font-body text-text-secondary leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="bg-brand-primary px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="bg-white rounded-lg p-1">
            <img src={LOGO_URL} alt="LocaLS Logo" className="h-8 w-auto object-contain" />
          </div>
        </Link>
        <Link
          to="/"
          className="flex items-center gap-1.5 font-body font-semibold text-sm text-white hover:text-white/80 transition-colors"
          data-testid="privacy-back-home"
        >
          <ArrowLeft size={15} />
          Back to Home
        </Link>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-14">
        {/* Title block */}
        <div className="mb-12">
          <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-brand-primary">
            Legal
          </span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mt-2 mb-3">
            Privacy Policy
          </h1>
          <p className="font-body text-text-muted text-sm">
            Last updated: January 2026
          </p>
        </div>

        <div className="w-12 h-1 bg-brand-primary rounded-full mb-10" />

        <Section title="1. Who We Are">
          <p>
            LocaLS is a hyperlocal marketplace built for apartment communities in India, operated by
            LitSpark Solutions. We connect residents who wish to buy and sell products or services within
            their own society.
          </p>
          <p>
            When we say "LocaLS", "we", "us", or "our" in this policy, we mean LitSpark Solutions and
            the LocaLS platform.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>
            <strong className="text-text-primary">From customers:</strong> Your name, phone number,
            email address, apartment name, and room number. We also collect the items you add to your
            cart and the orders you place.
          </p>
          <p>
            <strong className="text-text-primary">From vendors:</strong> Your name, email address,
            phone number, apartment affiliation, and profile details (store description and photo).
          </p>
          <p>
            <strong className="text-text-primary">Automatically:</strong> Basic usage data such as
            pages visited and device type, used solely to improve the platform experience.
          </p>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Facilitate orders and deliveries between buyers and sellers in your community.</li>
            <li>Send you OTP messages for authentication.</li>
            <li>Allow vendors to contact you about your orders.</li>
            <li>Improve and personalise your experience on the platform.</li>
            <li>Communicate important updates about your account or orders.</li>
          </ul>
          <p>We do not sell your personal data to any third party.</p>
        </Section>

        <Section title="4. Sharing of Information">
          <p>
            Your order details — including your name, apartment, and room number — are shared with the
            specific vendor fulfilling your order. This is necessary for delivery and pickup coordination.
          </p>
          <p>
            We do not share your information with advertisers, data brokers, or unrelated third parties.
          </p>
        </Section>

        <Section title="5. Data Storage and Security">
          <p>
            Your data is stored on secure cloud servers. We use industry-standard encryption for data
            in transit and at rest. Access to personal data is restricted to authorised personnel only.
          </p>
          <p>
            Authentication is handled via time-limited OTP codes and JWT tokens. We do not store
            plaintext passwords.
          </p>
        </Section>

        <Section title="6. Your Rights">
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate data.</li>
            <li>Request deletion of your account and associated data.</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{" "}
            <a
              href="mailto:privacy@litspark.solutions"
              className="text-brand-primary underline hover:text-brand-primary/80 transition-colors"
            >
              privacy@litspark.solutions
            </a>
            .
          </p>
        </Section>

        <Section title="7. Cookies">
          <p>
            LocaLS uses session cookies strictly for authentication purposes (keeping you logged in).
            We do not use tracking or advertising cookies.
          </p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>
            LocaLS is not intended for use by anyone under the age of 13. We do not knowingly collect
            personal information from children.
          </p>
        </Section>

        <Section title="9. Delete Account &amp; Delete Your Data">
          <p>
            As a customer, you have the right to permanently delete your account and all personal data
            associated with it. This includes your profile, order history, cart data, and any other
            information we hold about you.
          </p>
          <p>
            <strong className="text-text-primary">How to request deletion:</strong> Send an email to{" "}
            <a
              href="mailto:privacy@litspark.solutions"
              className="text-brand-primary underline hover:text-brand-primary/80 transition-colors"
            >
              privacy@litspark.solutions
            </a>{" "}
            from your registered email address with the subject line{" "}
            <span className="font-mono text-sm bg-brand-primary/10 text-brand-primary px-1.5 py-0.5 rounded">
              Delete My Account
            </span>
            . We will process your request within <strong className="text-text-primary">7 business days</strong>.
          </p>
          <p>
            <strong className="text-text-primary">What gets deleted:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Your name, phone number, email address, and apartment details.</li>
            <li>Your cart items and saved preferences.</li>
            <li>Your order history and associated delivery information.</li>
          </ul>
          <p>
            <strong className="text-text-primary">What is retained:</strong> Anonymised, non-identifiable
            transaction records may be retained for legal, accounting, or fraud-prevention purposes as
            required by applicable law. These records cannot be traced back to you.
          </p>
          <p>
            Once your account is deleted, this action is <strong className="text-text-primary">irreversible</strong>.
            You will need to create a new account if you wish to use LocaLS again.
          </p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will revise the
            "Last updated" date at the top. We encourage you to review this page periodically.
          </p>
        </Section>

        <Section title="11. Contact Us">
          <p>
            If you have any questions about this Privacy Policy, please reach out to us at{" "}
            <a
              href="mailto:privacy@litspark.solutions"
              className="text-brand-primary underline hover:text-brand-primary/80 transition-colors"
            >
              privacy@litspark.solutions
            </a>
            .
          </p>
        </Section>
      </main>

      {/* Footer strip */}
      <footer className="bg-brand-primary text-white text-center py-6 px-6">
        <p className="font-body text-sm text-white/80">
          © 2026 LocaLS. All rights reserved. | Made with love for Indian communities.
        </p>
      </footer>
    </div>
  );
}
